import React, { useState } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, ArrowDownIcon, ArrowUpIcon, CalendarIcon, FileTextIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import PolicyChangeTimeline from '../components/PolicyChangeTimeline';
const PolicyUpdates = () => {
  const [timeframe, setTimeframe] = useState('6months');
  const [viewMode, setViewMode] = useState<'timeline' | 'list'>('timeline');
  const [showExportModal, setShowExportModal] = useState(false);
  // Sample timeline events
  const timelineEvents = [{
    id: 1,
    date: '2025-05-19',
    payer: 'Aetna',
    title: 'Electrical Stimulation for Pain',
    type: 'negative',
    impact: 'high',
    details: 'Added Freedom PNS System to list of experimental devices'
  }, {
    id: 2,
    date: '2025-05-15',
    payer: 'Blue Cross and Blue Shield of Louisiana',
    title: 'Implantable Peripheral Nerve Stimulation Devices as a Treatment for Pain',
    type: 'negative',
    impact: 'medium',
    details: 'Annual review with no substantive changes'
  }, {
    id: 3,
    date: '2025-05-05',
    payer: 'AmeriHealth New Jersey',
    title: 'Experimental/Investigational Services',
    type: 'negative',
    impact: 'low',
    details: 'Added CPT code 64590 to list of non-covered services'
  }, {
    id: 4,
    date: '2025-04-15',
    payer: 'Humana',
    title: 'Peripheral Nerve Stimulation',
    type: 'positive',
    impact: 'high',
    details: 'New coverage policy with specific criteria for PNS'
  }, {
    id: 5,
    date: '2025-03-20',
    payer: 'UnitedHealthcare',
    title: 'Electrical Stimulation for Pain Management',
    type: 'restricted',
    impact: 'high',
    details: 'Updated criteria for coverage with new restrictions'
  }, {
    id: 6,
    date: '2025-03-15',
    payer: 'Cigna',
    title: 'Nerve Stimulation Devices for the Treatment of Pain',
    type: 'restricted',
    impact: 'medium',
    details: 'Added new clinical criteria requirements'
  }, {
    id: 7,
    date: '2025-03-01',
    payer: 'Anthem',
    title: 'Peripheral Nerve and Peripheral Nerve Field Stimulation',
    type: 'negative',
    impact: 'high',
    details: 'Policy remains investigational for all indications'
  }, {
    id: 8,
    date: '2025-02-15',
    payer: 'Capital BlueCross',
    title: 'Peripheral Subcutaneous Field Stimulation (PSFS)',
    type: 'negative',
    impact: 'medium',
    details: 'Annual review with no substantive changes'
  }, {
    id: 9,
    date: '2025-02-01',
    payer: 'Highmark',
    title: 'Peripheral Nerve Stimulation',
    type: 'positive',
    impact: 'medium',
    details: 'New positive coverage policy established'
  }, {
    id: 10,
    date: '2025-01-15',
    payer: 'Kaiser Permanente',
    title: 'Peripheral Nerve Stimulation for Pain',
    type: 'restricted',
    impact: 'high',
    details: 'Updated criteria with new documentation requirements'
  }, {
    id: 11,
    date: '2025-01-10',
    payer: 'BCBS Michigan',
    title: 'Peripheral Subcutaneous Field Stimulation And Peripheral Nerve Stimulation',
    type: 'negative',
    impact: 'medium',
    details: 'Reaffirmed experimental classification'
  }, {
    id: 12,
    date: '2025-01-05',
    payer: 'Premera Blue Cross',
    title: 'Peripheral Nerve Stimulation',
    type: 'restricted',
    impact: 'medium',
    details: 'Added coverage for specific indications with restrictions'
  }, {
    id: 13,
    date: '2024-12-15',
    payer: 'Centene',
    title: 'Nerve Stimulation Devices',
    type: 'negative',
    impact: 'high',
    details: 'New policy classifying PNS as investigational'
  }, {
    id: 14,
    date: '2024-12-01',
    payer: 'Molina Healthcare',
    title: 'Pain Management Interventions',
    type: 'negative',
    impact: 'medium',
    details: 'Updated to explicitly exclude PNS for chronic pain'
  }, {
    id: 15,
    date: '2024-11-20',
    payer: 'BCBS Tennessee',
    title: 'Peripheral Nerve Stimulation',
    type: 'positive',
    impact: 'medium',
    details: 'Added coverage for specific nerve applications'
  }, {
    id: 16,
    date: '2024-11-01',
    payer: 'BCBS North Carolina',
    title: 'Peripheral Nerve Stimulation',
    type: 'restricted',
    impact: 'medium',
    details: 'Added limited coverage with significant restrictions'
  }, {
    id: 17,
    date: '2024-10-15',
    payer: 'BCBS Alabama',
    title: 'Peripheral Nerve Stimulation of the Trunk or Limbs for Treatment of Pain',
    type: 'positive',
    impact: 'high',
    details: 'New positive coverage policy established'
  }, {
    id: 18,
    date: '2024-10-01',
    payer: 'Health Net',
    title: 'Implantable Electrical Stimulators',
    type: 'negative',
    impact: 'medium',
    details: 'Maintained non-coverage position for PNS'
  }];
  // Filters for timeframe
  const handleTimeframeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTimeframe(e.target.value);
  };
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-1731">
      <div className="mb-6" data-id="element-1732">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1733">
          <div data-id="element-1734">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1735">Policy Updates</h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1736">
              Chronological history of all policy changes and updates
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-1737">
            <div className="p-1 bg-white rounded-lg shadow flex" data-id="element-1738">
              <button className={`p-2 ${viewMode === 'timeline' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'} rounded`} onClick={() => setViewMode('timeline')} title="Timeline View" data-id="element-1739">
                <CalendarIcon className="h-5 w-5" data-id="element-1740" />
              </button>
              <button className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'} rounded`} onClick={() => setViewMode('list')} title="List View" data-id="element-1741">
                <FileTextIcon className="h-5 w-5" data-id="element-1742" />
              </button>
            </div>
            <button className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 shadow flex items-center" onClick={() => setShowExportModal(true)} data-id="element-1743">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1744" />
              Export
            </button>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="flex flex-col items-stretch mb-6 space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4" data-id="element-1745">
        <div className="relative flex-grow" data-id="element-1746">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1747">
            <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-1748" />
          </div>
          <input type="text" className="block w-full py-2 pl-10 pr-3 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search policy updates..." data-id="element-1749" />
        </div>
        <div className="flex space-x-4" data-id="element-1750">
          <select className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={timeframe} onChange={handleTimeframeChange} data-id="element-1751">
            <option value="3months" data-id="element-1752">Last 3 Months</option>
            <option value="6months" data-id="element-1753">Last 6 Months</option>
            <option value="12months" data-id="element-1754">Last 12 Months</option>
            <option value="all" data-id="element-1755">All Time</option>
          </select>
          <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" data-id="element-1756">
            <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-1757" />
            Advanced Filters
          </button>
        </div>
      </div>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4 mb-6" data-id="element-1758">
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1759">
          <div className="p-3 rounded-full bg-blue-100 mr-4" data-id="element-1760">
            <FileTextIcon className="h-6 w-6 text-blue-600" data-id="element-1761" />
          </div>
          <div data-id="element-1762">
            <p className="text-sm text-gray-500" data-id="element-1763">Total Updates</p>
            <p className="text-xl font-semibold" data-id="element-1764">{timelineEvents.length}</p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1765">
          <div className="p-3 rounded-full bg-red-100 mr-4" data-id="element-1766">
            <ArrowDownIcon className="h-6 w-6 text-red-600" data-id="element-1767" />
          </div>
          <div data-id="element-1768">
            <p className="text-sm text-gray-500" data-id="element-1769">Negative Updates</p>
            <p className="text-xl font-semibold" data-id="element-1770">
              {timelineEvents.filter(e => e.type === 'negative').length}
            </p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1771">
          <div className="p-3 rounded-full bg-green-100 mr-4" data-id="element-1772">
            <ArrowUpIcon className="h-6 w-6 text-green-600" data-id="element-1773" />
          </div>
          <div data-id="element-1774">
            <p className="text-sm text-gray-500" data-id="element-1775">Positive Updates</p>
            <p className="text-xl font-semibold" data-id="element-1776">
              {timelineEvents.filter(e => e.type === 'positive').length}
            </p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1777">
          <div className="p-3 rounded-full bg-amber-100 mr-4" data-id="element-1778">
            <CalendarIcon className="h-6 w-6 text-amber-600" data-id="element-1779" />
          </div>
          <div data-id="element-1780">
            <p className="text-sm text-gray-500" data-id="element-1781">Last 30 Days</p>
            <p className="text-xl font-semibold" data-id="element-1782">
              {timelineEvents.filter(e => {
              const eventDate = new Date(e.date);
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              return eventDate >= thirtyDaysAgo;
            }).length}
            </p>
          </div>
        </div>
      </div>
      {/* Timeline View */}
      {viewMode === 'timeline' && <div className="bg-white rounded-lg shadow p-6" data-id="element-1783">
          <PolicyChangeTimeline events={timelineEvents} data-id="element-1784" />
        </div>}
      {/* List View */}
      {viewMode === 'list' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1785">
          <div className="overflow-x-auto" data-id="element-1786">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1787">
              <thead className="bg-gray-50" data-id="element-1788">
                <tr data-id="element-1789">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1790">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1791">
                    Payer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1792">
                    Policy Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1793">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1794">
                    Impact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1795">
                    Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1796">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1797">
                {timelineEvents.map(event => <tr key={event.id} className="hover:bg-gray-50" data-id="element-1798">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1799">
                      {event.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1800">
                      <div className="text-sm font-medium text-blue-600" data-id="element-1801">
                        {event.payer}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-1802">
                      <div className="text-sm text-gray-900" data-id="element-1803">{event.title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1804">
                      <StatusBadge status={event.type as any} data-id="element-1805" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1806">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${event.impact === 'high' ? 'bg-red-100 text-red-800' : event.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1807">
                        {event.impact.charAt(0).toUpperCase() + event.impact.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1808">
                      {event.details}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-1809">
                      <button className="text-blue-600 hover:text-blue-900" data-id="element-1810">
                        View
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 z-10 overflow-y-auto" data-id="element-1811">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1812">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-1813">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowExportModal(false)} data-id="element-1814"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1815">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-1816">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-1817">
                <div className="sm:flex sm:items-start" data-id="element-1818">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-1819">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-1820">
                      Export Policy Updates
                    </h3>
                    <div className="mt-4" data-id="element-1821">
                      <div className="mb-4" data-id="element-1822">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1823">
                          Export Format
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1824">
                          <option data-id="element-1825">PDF Report</option>
                          <option data-id="element-1826">Excel Spreadsheet</option>
                          <option data-id="element-1827">CSV File</option>
                        </select>
                      </div>
                      <div className="mb-4" data-id="element-1828">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1829">
                          Time Range
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1830">
                          <option data-id="element-1831">Last 3 Months</option>
                          <option data-id="element-1832">Last 6 Months</option>
                          <option data-id="element-1833">Last 12 Months</option>
                          <option data-id="element-1834">All Time</option>
                          <option data-id="element-1835">Custom Range</option>
                        </select>
                      </div>
                      <div className="mb-4" data-id="element-1836">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1837">
                          Include Details
                        </label>
                        <div className="mt-2" data-id="element-1838">
                          <div className="flex items-center" data-id="element-1839">
                            <input id="details-summary" name="details" type="radio" className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" checked data-id="element-1840" />
                            <label htmlFor="details-summary" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-1841">
                              Summary Only
                            </label>
                          </div>
                          <div className="flex items-center mt-2" data-id="element-1842">
                            <input id="details-full" name="details" type="radio" className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1843" />
                            <label htmlFor="details-full" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-1844">
                              Full Details
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-1845">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1846">
                  Export
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowExportModal(false)} data-id="element-1847">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default PolicyUpdates;